import { useState } from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";

/**
 * Combobox that suggests existing options AND lets the user type a NEW value.
 * Works by VALUE STRING (e.g. a channel name): `value` is the current string,
 * `onValueChange(str)` fires on pick / create / clear. `options` is
 * [{ value, label, render? }] — matching is by label (case-insensitive).
 */
export const CreatableCombobox = ({
  value,
  onValueChange,
  options = [],
  placeholder = "Select or type…",
  emptyText = "Type a name to add it.",
  createLabel = "Add",
  className = "",
  "data-testid": testId,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const q = query.trim();
  const labelOf = (o) => o.label ?? o.value;
  const filtered = q ? options.filter((o) => labelOf(o).toLowerCase().includes(q.toLowerCase())) : options;
  const exists = options.some((o) => labelOf(o).toLowerCase() === q.toLowerCase());

  const choose = (v) => { onValueChange(v); setOpen(false); setQuery(""); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={`w-full justify-between font-normal ${!value ? "text-muted-foreground" : ""} ${className}`}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search or type a new name…" value={query} onValueChange={setQuery} />
          <CommandList>
            {filtered.length === 0 && !q && <CommandEmpty>{emptyText}</CommandEmpty>}
            {/* Options + the create row come FIRST so cmdk's select-first-item pins the
                highlight to a real choice — pressing Enter confirms it. The Clear row is
                rendered LAST so Enter never silently wipes an already-selected value. */}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((opt) => (
                  <CommandItem key={opt.value} value={opt.value} onSelect={() => choose(labelOf(opt))}>
                    <Check className={`mr-2 h-4 w-4 ${value === labelOf(opt) ? "opacity-100" : "opacity-0"}`} />
                    {opt.render || <span className="truncate">{labelOf(opt)}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {q && !exists && (
              <CommandGroup>
                <CommandItem value={`__create__:${q}`} onSelect={() => choose(q)}>
                  <Plus className="mr-2 h-4 w-4" /> {createLabel} “{q}”
                </CommandItem>
              </CommandGroup>
            )}
            {value && (
              <CommandGroup>
                <CommandItem value="__clear__" onSelect={() => choose("")}>
                  <X className="mr-2 h-4 w-4 text-slate-400" /> <span className="text-slate-500">Clear</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CreatableCombobox;
